import {
  type Task,
  type TaskEvent,
  taskEventsOptions,
  useCreateTaskNoteMutation,
} from '@cartwheel/api/v1'
import { Button, Textarea } from '@cartwheel/components'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

type HistoryEvent = TaskEvent & {
  displayLabel?: string
  displayName?: string
}

type EventGroup = {
  key: string
  label: string
  events: HistoryEvent[]
}

const DAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
})

const TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
})

function eventDayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

function isSameDay(left: Date, right: Date): boolean {
  return eventDayKey(left) === eventDayKey(right)
}

function eventDayLabel(date: Date): string {
  const today = new Date()
  if (isSameDay(date, today)) return 'Today'

  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (isSameDay(date, yesterday)) return 'Yesterday'

  return DAY_FORMATTER.format(date)
}

function eventsShareActor(events: TaskEvent[]): boolean {
  const actorId = events[0]?.user?.id
  return Boolean(actorId && events.every((event) => event.user?.id === actorId))
}

function eventsAreClose(events: TaskEvent[]): boolean {
  const timestamps = events.map((event) => new Date(event.created_at).getTime())
  return Math.max(...timestamps) - Math.min(...timestamps) <= 2000
}

function collapseOwnershipEvents(events: TaskEvent[]): HistoryEvent[] {
  const collapsedEvents: HistoryEvent[] = []

  for (let index = 0; index < events.length; index += 1) {
    const current = events[index]
    if (!current) continue

    const next = events[index + 1]
    const third = events[index + 2]

    if (
      current.name === 'claimed' &&
      next?.name === 'assigned' &&
      third?.name === 'unclaimed' &&
      eventsShareActor([current, next, third]) &&
      eventsAreClose([current, next, third])
    ) {
      collapsedEvents.push({
        ...current,
        displayLabel: 'Task taken over',
        displayName: 'taken_over',
      })
      index += 2
      continue
    }

    if (
      current.name === 'claimed' &&
      next?.name === 'assigned' &&
      eventsShareActor([current, next]) &&
      eventsAreClose([current, next])
    ) {
      collapsedEvents.push({ ...current, displayLabel: 'Task started', displayName: 'started' })
      index += 1
      continue
    }

    if (
      current.name === 'unassigned' &&
      next?.name === 'unclaimed' &&
      eventsShareActor([current, next]) &&
      eventsAreClose([current, next])
    ) {
      collapsedEvents.push({ ...current, displayLabel: 'Task released', displayName: 'released' })
      index += 1
      continue
    }

    if (
      current.name === 'assigned' &&
      next?.name === 'unclaimed' &&
      eventsShareActor([current, next]) &&
      eventsAreClose([current, next])
    ) {
      collapsedEvents.push({
        ...current,
        displayLabel: current.subject?.name
          ? `Reassigned to ${current.subject.name}`
          : 'Task reassigned',
        displayName: 'assigned',
      })
      index += 1
      continue
    }

    collapsedEvents.push(current)
  }

  return collapsedEvents
}

function groupEventsByDay(events: HistoryEvent[]): EventGroup[] {
  const groups: EventGroup[] = []

  for (const event of events) {
    const date = new Date(event.created_at)
    const key = eventDayKey(date)
    const currentGroup = groups.at(-1)

    if (currentGroup?.key === key) {
      currentGroup.events.push(event)
    } else {
      groups.push({ key, label: eventDayLabel(date), events: [event] })
    }
  }

  return groups
}

function eventLabel(event: HistoryEvent): string {
  if (event.displayLabel) return event.displayLabel

  const subjectName = event.subject?.name
  switch (event.name) {
    case 'assigned':
      return subjectName ? `Assigned to ${subjectName}` : 'Task assigned'
    case 'unassigned':
      return 'Task unassigned'
    case 'claimed':
      return 'Task claimed'
    case 'unclaimed':
      return 'Task released'
    case 'created':
      return 'Task created'
    case 'completed':
      return 'Task completed'
    case 'canceled':
      return 'Task canceled'
    default:
      return event.name
  }
}

const DOT_STYLES: Record<string, string> = {
  started: 'bg-sea',
  claimed: 'bg-sea',
  assigned: 'bg-charcoal-25',
  taken_over: 'bg-charcoal-25',
  released: 'bg-charcoal-25',
  unassigned: 'bg-charcoal-25',
  unclaimed: 'bg-charcoal-25',
  completed: 'bg-forest',
  canceled: 'bg-brick-light',
}

function EventDot({ name }: { name: string }) {
  const style = DOT_STYLES[name] ?? 'bg-charcoal-25'
  return (
    <div
      className={`size-2 shrink-0 rounded-full ${style}`}
      data-event-name={name}
      aria-hidden="true"
    />
  )
}

function actorInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
}

function EventActor({ actor }: { actor: NonNullable<TaskEvent['user']> }) {
  return (
    <span
      className="inline-flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-sand text-[9px] font-semibold text-charcoal-50"
      role="img"
      aria-label={`Actor: ${actor.name}`}
      title={actor.name}
    >
      {actor.avatar_url ? (
        <img src={actor.avatar_url} alt="" width={20} height={20} className="size-5 object-cover" />
      ) : (
        actorInitials(actor.name)
      )}
    </span>
  )
}

function EventTime({ event }: { event: TaskEvent }) {
  return (
    <time
      className="shrink-0 text-xs tabular-nums text-charcoal-50"
      dateTime={event.created_at}
      title={new Date(event.created_at).toLocaleString()}
    >
      {TIME_FORMATTER.format(new Date(event.created_at))}
    </time>
  )
}

function NoteEventRow({ event }: { event: TaskEvent }) {
  return (
    <div className="flex gap-3">
      {event.user?.avatar_url ? (
        <img
          src={event.user.avatar_url}
          alt={event.user.name}
          width={32}
          height={32}
          className="mt-0.5 size-8 shrink-0 rounded-full object-cover"
        />
      ) : (
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-sand text-xs font-semibold text-charcoal-50">
          {event.user?.name?.charAt(0) ?? '?'}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-charcoal">
            {event.user?.name ?? 'Unknown'}
          </span>
          <EventTime event={event} />
        </div>
        <p className="mt-0.5 break-words text-sm leading-[1.5] text-charcoal-50">{event.body}</p>
      </div>
    </div>
  )
}

function SystemEventRow({ event }: { event: HistoryEvent }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex w-8 shrink-0 justify-center">
        <EventDot name={event.displayName ?? event.name} />
      </div>
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <p className="min-w-0 break-words text-sm font-medium text-charcoal">
            {eventLabel(event)}
          </p>
          {event.user && <EventActor actor={event.user} />}
        </div>
        <EventTime event={event} />
      </div>
    </div>
  )
}

function EventRow({ event }: { event: HistoryEvent }) {
  if (event.name === 'note_added') return <NoteEventRow event={event} />
  return <SystemEventRow event={event} />
}

function NoteInput({
  taskId,
  body,
  onBodyChange,
}: {
  taskId: string
  body: string
  onBodyChange: (body: string) => void
}) {
  const queryClient = useQueryClient()

  const mutation = useCreateTaskNoteMutation({
    onSuccess: () => {
      onBodyChange('')
      queryClient.invalidateQueries({
        queryKey: taskEventsOptions({ path: { task_id: taskId } }).queryKey,
      })
    },
  })

  function handleSubmit() {
    const trimmed = body.trim()
    if (!trimmed || mutation.isPending) return
    mutation.mutate({ path: { task_id: taskId }, body: { body: trimmed } })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const inputId = `task-note-${taskId}`
  const hintId = `${inputId}-hint`

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label htmlFor={inputId} className="text-sm font-medium text-charcoal">
          Add note
        </label>
        <span id={hintId} className="text-xs text-charcoal-50">
          <kbd className="font-sans">⌘/Ctrl + Enter</kbd> to submit
        </span>
      </div>
      <div className="relative">
        <Textarea
          id={inputId}
          aria-describedby={hintId}
          value={body}
          onChange={(event) => {
            onBodyChange(event.target.value)
          }}
          onKeyDown={handleKeyDown}
          placeholder="Write a note…"
          rows={3}
          className="resize-none pb-8 text-sm"
        />
        <Button
          variant="none"
          onClick={handleSubmit}
          disabled={!body.trim() || mutation.isPending}
          aria-label="Submit note"
          className="absolute bottom-2 right-2 flex size-7 items-center justify-center rounded text-forest transition-colors hover:bg-sand hover:text-forest-light"
        >
          <i className="fa-solid fa-paper-plane-top text-sm" aria-hidden="true" />
        </Button>
      </div>
    </div>
  )
}

export function TaskHistory({
  task,
  events,
  noteDraft,
  onNoteDraftChange,
}: {
  task: Task
  events: TaskEvent[]
  noteDraft?: string
  onNoteDraftChange?: (draft: string) => void
}) {
  const [localNoteDraft, setLocalNoteDraft] = useState('')
  const body = noteDraft ?? localNoteDraft
  const setBody = (draft: string) => {
    setLocalNoteDraft(draft)
    onNoteDraftChange?.(draft)
  }
  const eventGroups = groupEventsByDay(collapseOwnershipEvents(events))

  return (
    <section
      className="flex max-h-[min(420px,45vh)] min-h-0 flex-col overflow-hidden min-[720px]:max-h-none min-[720px]:min-h-[280px] min-[720px]:flex-1"
      aria-labelledby={`task-history-${task.id}`}
    >
      <h3
        id={`task-history-${task.id}`}
        className="mb-4 shrink-0 text-base font-semibold text-charcoal"
      >
        History
      </h3>

      <div className="mb-4 shrink-0">
        <NoteInput key={task.id} taskId={task.id} body={body} onBodyChange={setBody} />
      </div>

      {events.length === 0 ? (
        <p className="text-sm text-charcoal-50">No activity yet.</p>
      ) : (
        <section
          className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain pr-3 [scrollbar-gutter:stable]"
          aria-label="Task history events"
        >
          {eventGroups.map((group) => {
            const headingId = `task-history-${task.id}-${group.key}`

            return (
              <section key={group.key} aria-labelledby={headingId}>
                <h4
                  id={headingId}
                  className="sticky top-0 z-[1] bg-white pb-2 text-xs font-semibold uppercase tracking-wide text-charcoal-50"
                >
                  {group.label}
                </h4>
                <ol className="space-y-3">
                  {group.events.map((event) => (
                    <li key={event.id}>
                      <EventRow event={event} />
                    </li>
                  ))}
                </ol>
              </section>
            )
          })}
        </section>
      )}
    </section>
  )
}
